import { Flex, Typography, Spin, Tag, List, Avatar, Modal, Tree } from 'antd';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useSelector } from 'react-redux';
import {
  selectMachine,
  selectConversationLogs,
  selectConversationLogsLoading,
  selectConversationLogsError,
  fetchConversationLogs,
} from '../../../../slices/machines';
import { useAppDispatch } from '../../../../store';
import { ConversationLog } from '../../../../types/events';
import { UserOutlined } from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import { EVENT_STATUS_CONFIG } from '../../../../constants';

interface LogsTabProps {
  machineId?: string;
}

interface LogTreeNode extends ConversationLog {
  children?: LogTreeNode[];
}

dayjs.extend(utc);
dayjs.extend(relativeTime);

const TYPE_LABELS: Record<string, string> = {
  diagnosis: 'Template',
  routine: 'Routine',
  step: 'Step',
};

const getStatusTag = (statusCode?: string) => {
  if (!statusCode) return null;
  const config = EVENT_STATUS_CONFIG[statusCode] || {
    text: statusCode,
    color: 'default',
  };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const buildTree = (items: ConversationLog[]): LogTreeNode[] => {
  // Group by template -> routine -> step hierarchy
  const templates = new Map<number, LogTreeNode>();
  const routines = new Map<number, LogTreeNode>();

  // Sort by type priority: diagnosis (template) first, then routine, then step
  const sortedItems = [...items].sort((a, b) => {
    const priority = { diagnosis: 0, routine: 1, step: 2 };
    return (
      (priority[a.type as keyof typeof priority] ?? 3) -
      (priority[b.type as keyof typeof priority] ?? 3)
    );
  });

  sortedItems.forEach((item) => {
    const node: LogTreeNode = { ...item, children: [] };

    if (item.type === 'diagnosis') {
      templates.set(item.id, node);
    } else if (item.type === 'routine') {
      routines.set(item.id, node);
      const parent = templates.get(item.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        templates.set(item.id, node);
      }
    } else if (item.type === 'step') {
      // Find parent routine by matching routineId AND parentId (same diagnosis session)
      const parentRoutine = Array.from(routines.values()).find(
        (r) => r.routineId === item.routineId && r.parentId === item.parentId
      );
      if (parentRoutine) {
        parentRoutine.children = parentRoutine.children || [];
        parentRoutine.children.push(node);
      }
      // Orphan steps (no parent routine found) are intentionally dropped
    }
  });

  // Sort children by createdAt
  const sortChildren = (nodes: LogTreeNode[]) => {
    nodes.forEach((node) => {
      if (node.children?.length) {
        node.children.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        sortChildren(node.children);
      }
    });
  };

  const result = Array.from(templates.values());
  sortChildren(result);

  // Sort roots by createdAt descending
  return result.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

const LogsTab = ({ machineId }: LogsTabProps) => {
  const dispatch = useAppDispatch();
  const machine = useSelector(selectMachine);
  const allLogs = useSelector(selectConversationLogs);
  const loading = useSelector(selectConversationLogsLoading);
  const error = useSelector(selectConversationLogsError);
  const [selectedLog, setSelectedLog] = useState<LogTreeNode | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const buildGroupedLogs = (logs: ConversationLog[]) => {
    const tree = buildTree(logs);
    const targetTemplateId = machine?.templateID;
    return tree.filter(
      (root) =>
        root.templateId === targetTemplateId ||
        Number(root.templateId) === Number(targetTemplateId)
    );
  };

  const groupedLogs = buildGroupedLogs(allLogs);

  const buildTreeData = (nodes: LogTreeNode[]): DataNode[] => {
    return nodes.map((node) => ({
      key: String(node.id),
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{ width: '100%', paddingRight: 16 }}
        >
          <Flex vertical gap={2}>
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              {!node.templateName && (TYPE_LABELS[node.type] || node.type)}
            </Typography.Text>
            <Typography.Text strong={node.type === 'diagnosis'}>
              {node.stepName ||
                node.routineName ||
                node.templateName ||
                `Log #${node.id}`}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {dayjs.utc(node.createdAt).local().format('YYYY-MM-DD HH:mm:ss')}{' '}
              · {node.creatorName}
            </Typography.Text>
          </Flex>
          {node.type !== 'step' && getStatusTag(node.statusCode)}
        </Flex>
      ),
      children: node.children?.length
        ? buildTreeData(node.children)
        : undefined,
    }));
  };

  useEffect(() => {
    dispatch(fetchConversationLogs('*'));
  }, [dispatch]);

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours > 0 ? `${hours}h ` : ''}${remainingMinutes > 0 ? `${remainingMinutes}m ` : ''}${remainingSeconds}s`;
  };

  const renderLogItem = (log: LogTreeNode) => {
    const childCount = log.children?.length ?? 0;
    const childType =
      log.type === 'diagnosis'
        ? 'routine(s)'
        : log.type === 'routine'
          ? 'step(s)'
          : '';

    return (
      <Flex vertical gap={4}>
        <Flex justify="space-between" align="center">
          <Typography.Text strong>
            {log.routineName || log.templateName || `Log #${log.id}`}
          </Typography.Text>
          {getStatusTag(log.statusCode)}
        </Flex>
        <Flex gap={16} wrap>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs.utc(log.createdAt).local().format('YYYY-MM-DD HH:mm:ss')}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {log.creatorName}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatDuration(log.durationSystem)}
          </Typography.Text>
          {childCount > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {childCount} {childType}
            </Typography.Text>
          )}
        </Flex>
      </Flex>
    );
  };

  const handleItemClick = (log: LogTreeNode) => {
    setSelectedLog(log);
    setModalVisible(true);
  };

  const renderStepsWorkflow = (steps: LogTreeNode[]) => {
    return (
      <Flex vertical gap={8} style={{ padding: '16px 0' }}>
        <Typography.Text strong style={{ marginBottom: 8 }}>
          Steps Workflow
        </Typography.Text>
        <Flex wrap gap={8} align="center">
          {steps.map((step, index) => (
            <Flex key={step.id} vertical align="center" gap={4}>
              <Tag
                color={EVENT_STATUS_CONFIG[step.statusCode]?.color || 'default'}
              >
                {step.stepName || `Step ${index + 1}`}
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {formatDuration(step.durationSystem)}
              </Typography.Text>
              {index < steps.length - 1 && (
                <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                  ↓
                </Typography.Text>
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
    );
  };

  const renderLogDetails = (
    log: ConversationLog & { children?: LogTreeNode[] }
  ) => (
    <Flex vertical gap={8} style={{ padding: '8px 0' }}>
      <Flex gap={16} wrap>
        <Typography.Text type="secondary" strong>
          {log.creatorName || '-'} {'  '}{' '}
          {dayjs.utc(log.createdAt).local().format('YYYY-MM-DD HH:mm:ss')}
        </Typography.Text>
      </Flex>
      {log.routineName && (
        <Flex gap={16} wrap>
          <Typography.Text strong>Routine:</Typography.Text>
          <Typography.Text>{log.routineName}</Typography.Text>
        </Flex>
      )}
      {log.stepName && (
        <Flex gap={16} wrap>
          <Typography.Text strong>Step:</Typography.Text>
          <Typography.Text>{log.stepName}</Typography.Text>
        </Flex>
      )}
      <Flex gap={16} wrap>
        <Typography.Text strong>Duration (System):</Typography.Text>
        <Typography.Text>{formatDuration(log.durationSystem)}</Typography.Text>
      </Flex>
      {log.rate !== undefined && log.rate !== null && (
        <Flex gap={16} wrap>
          <Typography.Text strong>Rate:</Typography.Text>
          <Typography.Text>{log.rate}/5</Typography.Text>
        </Flex>
      )}
      {log.type === 'routine' &&
        log.children &&
        log.children.length > 0 &&
        renderStepsWorkflow(log.children)}
    </Flex>
  );

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ padding: 40 }}>
        <Spin />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" style={{ padding: 40 }}>
        <Typography.Text type="danger">{error}</Typography.Text>
      </Flex>
    );
  }

  if (allLogs.length === 0) {
    return (
      <Flex justify="center" align="center" style={{ padding: 40 }}>
        <Typography.Text type="secondary">No logs available</Typography.Text>
      </Flex>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <List
        itemLayout="horizontal"
        dataSource={groupedLogs}
        renderItem={(log) => (
          <List.Item
            style={{ cursor: 'pointer' }}
            onClick={() => handleItemClick(log)}
          >
            <List.Item.Meta
              avatar={<Avatar icon={<UserOutlined />} />}
              title={renderLogItem(log)}
            />
          </List.Item>
        )}
      />
      <Modal
        title={
          <Flex gap={8}>
            {selectedLog?.routineName ||
              selectedLog?.templateName ||
              `Log #${selectedLog?.id}`}
            {getStatusTag(selectedLog?.statusCode)}
          </Flex>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedLog && (
          <Flex vertical gap={16}>
            {renderLogDetails(selectedLog)}
            {selectedLog.type === 'diagnosis' &&
              selectedLog.children &&
              selectedLog.children.length > 0 && (
                <div>
                  <Typography.Text
                    strong
                    style={{ marginBottom: 8, display: 'block' }}
                  >
                    Routines
                  </Typography.Text>
                  <Tree
                    showLine
                    defaultExpandAll
                    treeData={buildTreeData(selectedLog.children)}
                  />
                </div>
              )}
          </Flex>
        )}
      </Modal>
    </div>
  );
};

export default LogsTab;
